import { isToolUIPart } from "ai";
const STORAGE_PREFIX = "clientToolRuns:";
const PENDING_POLLS = 5;
const ANSWER_RETRIES = 5;
const RETRY_DELAY_MS = 500;
const MAX_STORED_CHATS = 20;
const logDev = (...args)=>{
    if (process.env.NODE_ENV === "development") console.info("[clientToolGate]", ...args);
};
const sleep = (ms)=>new Promise((resolve)=>{
        setTimeout(resolve, ms);
    });
function readIds(storage, key) {
    try {
        const stored = storage?.getItem(key);
        return stored ? JSON.parse(stored) : [];
    } catch  {
        return [];
    }
}
function pruneOldChats(storage) {
    try {
        if (!storage) return;
        const keys = [];
        for(let index = 0; index < storage.length; index++){
            const name = storage.key(index);
            if (name?.startsWith(STORAGE_PREFIX)) keys.push(name);
        }
        keys.sort().slice(0, -MAX_STORED_CHATS).forEach((name)=>storage.removeItem(name));
    } catch  {
        return;
    }
}
function isSettled(snapshot, toolCallId) {
    return snapshot.messages.some((message)=>message.parts.some((part)=>isToolUIPart(part) && part.toolCallId === toolCallId && (part.state === "output-available" || part.state === "output-error")));
}
export function createClientToolGate({ chatUrl, chatId, fetch, storage, run, isMutating, answer, onError }) {
    const storageKey = `${STORAGE_PREFIX}${chatId}`;
    const executed = new Set();
    let isReplayStream = false;
    let streamGeneration = 0;
    let isDisposed = false;
    let queue = Promise.resolve();
    const queued = new Map();
    const hasRun = (toolCallId)=>executed.has(toolCallId) || readIds(storage, storageKey).includes(toolCallId);
    const remember = (toolCallId)=>{
        executed.add(toolCallId);
        if (!storage) return false;
        try {
            const ids = new Set(readIds(storage, storageKey));
            ids.add(toolCallId);
            storage.setItem(storageKey, JSON.stringify([
                ...ids
            ]));
            pruneOldChats(storage);
            return true;
        } catch  {
            return false;
        }
    };
    const cancel = ()=>{
        streamGeneration += 1;
    };
    const trackedFetch = (input, init)=>{
        const url = String(input);
        const isPost = init?.method === "POST";
        if (url.includes("/stream?")) {
            isReplayStream = true;
        } else if (isPost && (url.endsWith("/messages") || url.endsWith("/stop"))) {
            isReplayStream = false;
            cancel();
        }
        return fetch(input, init);
    };
    const isAwaitingAnswer = async (toolCallId)=>{
        for(let poll = 0; poll < PENDING_POLLS; poll++){
            const response = await fetch(chatUrl);
            if (!response.ok) return false;
            const snapshot = await response.json();
            if (isSettled(snapshot, toolCallId)) return false;
            const pending = snapshot.pendingInput?.toolCallId;
            if (pending === toolCallId) return true;
            if (pending && queued.has(pending) && !hasRun(pending)) return "later";
            if (!pending || !hasRun(pending)) return false;
            await sleep(RETRY_DELAY_MS);
        }
        return false;
    };
    const isCurrent = (generation)=>!isDisposed && generation === streamGeneration;
    const deliver = async (toolCallId, output, generation)=>{
        for(let attempt = 0;; attempt++){
            try {
                await answer(toolCallId, {
                    output
                });
                return;
            } catch (error) {
                if (!isCurrent(generation)) return;
                if (attempt >= ANSWER_RETRIES) throw error;
                await sleep(RETRY_DELAY_MS * 2 ** attempt);
            }
        }
    };
    const execute = async (request, isReplay, generation)=>{
        const { toolCallId, toolName } = request;
        if (hasRun(toolCallId)) return false;
        if (!isCurrent(generation)) {
            logDev("dropped, superseded by a new message", toolName, toolCallId);
            return false;
        }
        if (isReplay && isMutating(toolName)) {
            const awaiting = await isAwaitingAnswer(toolCallId);
            if (awaiting === "later") {
                logDev("deferred behind the pending card", toolName, toolCallId);
                return true;
            }
            if (!awaiting) {
                logDev("dropped, not awaiting an answer", toolName, toolCallId);
                return false;
            }
        }
        if (!isCurrent(generation) || hasRun(toolCallId)) return false;
        const isPersisted = remember(toolCallId);
        if (isReplay && !isPersisted && isMutating(toolName)) {
            logDev("dropped, cannot record a replayed edit", toolName, toolCallId);
            return false;
        }
        const output = await Promise.resolve().then(()=>run(toolName, request.input, toolCallId)).catch((error)=>({
                error: error instanceof Error ? error.message : String(error)
            }));
        await deliver(toolCallId, output, generation);
        return false;
    };
    const settle = (toolCallId)=>{
        const left = (queued.get(toolCallId) ?? 1) - 1;
        if (left > 0) queued.set(toolCallId, left);
        else queued.delete(toolCallId);
    };
    const enqueue = (request, isReplay, generation)=>{
        queue = queue.then(async ()=>{
            const isDeferred = await execute(request, isReplay, generation).catch((error)=>{
                settle(request.toolCallId);
                throw error;
            });
            if (isDeferred) setTimeout(()=>enqueue(request, isReplay, generation), RETRY_DELAY_MS);
            else settle(request.toolCallId);
        }).catch((error)=>{
            if (!isDisposed) onError(error);
        });
    };
    const handle = (request)=>{
        queued.set(request.toolCallId, (queued.get(request.toolCallId) ?? 0) + 1);
        enqueue(request, isReplayStream, streamGeneration);
    };
    const dispose = ()=>{
        isDisposed = true;
    };
    return {
        fetch: trackedFetch,
        handle,
        cancel,
        dispose
    };
}
